import React, { useState, useMemo } from 'react';
import {
  shape, string, arrayOf, number
} from 'prop-types';
import classNames from 'classnames/bind';

import { QueryProvider } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { Image } from '@thd-olt-component-react/image';
import { Carousel } from '@thd-olt-component-react/carousel';
import { ProductPod, ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';

import { useSpecialBuy } from '../../context/SpecialBuyProvider';
import { useSearchProducts, MAX_NUM_ITEMS_TO_LOAD } from '../../hooks/useSearchProducts';
import styles from './category-products.module.scss';

const cNames = classNames.bind(styles);

const optionsHandler = ({ options }) => {
  return {
    ...options,
    fetchPolicy: 'cache-only'
  };
};

const productPodWidth = 220; // used to configure carousel. keep in sync with sass consts

const buildProductPodArray = ({
  storeId, itemIdsToDisplay, itemIdsWithSwatches, categoryName, specialBuyType
}) => {
  if (!Array.isArray(itemIdsToDisplay)) return [];

  const analyticsData = {
    section: specialBuyType === 'sbotd'
      ? 'SBOTD banner'
      : 'SBOTW banner',
    sharedSection: categoryName,
  };

  return itemIdsToDisplay.map((itemId, index) => {
    const productPodClassNames = cNames('special-buy-category__product-pod', {
      'special-buy-category__product-pod__see-more-options--hidden': !itemIdsWithSwatches.has(itemId),
    });

    return (
      <ProductPod
        className={productPodClassNames}
        itemId={itemId}
        storeId={storeId}
        titleMaxLine={2}
        brandMaxLine={1} // TODO: this prop doesn't appear to be working
        hideFavorites
        hidePriceBadge
        hideShippingInfo
        hideShopThisCollection
        hideStoreInfo
        hideFulfillment
        hidePromotions
        showExactPriceOnLoad
        showSeeMoreOptions
        doNotShowChooseYourOptions
        position={index}
        key={`special-buy-${itemId}`}
        analyticsData={analyticsData}
      />
    );
  });
};

const CategoryProducts = ({ category, bannerWidth }) => {
  const { selectedCategory, specialBuyType, specialBuyEndpoint, simpleBanner } = useSpecialBuy();
  const { storeId } = useStore();
  const [isFetchAllItemIds, setIsFetchAllItemIds] = useState(false);
  const isSelected = Object.is(selectedCategory, category);

  const {
    itemIdsToDisplay,
    isLoading,
  } = useSearchProducts(category, isFetchAllItemIds, bannerWidth);

  const carouselContent = useMemo(() => {
    if (!itemIdsToDisplay?.length) return [];
    const newCarouselContent = [];

    const itemIdsWithSwatches = new Set();
    category.products.slice(0, MAX_NUM_ITEMS_TO_LOAD).forEach((product) => {
      if (product.hasSwatches) itemIdsWithSwatches.add(String(product.itemId));
    });

    const productPods = buildProductPodArray({
      itemIdsToDisplay,
      storeId,
      itemIdsWithSwatches,
      categoryName: category.name,
      specialBuyType,
    });

    newCarouselContent.push(...productPods);

    const maxPodsToShow = Math.min(category.products.length, MAX_NUM_ITEMS_TO_LOAD);
    const numPlaceholderPods = Math.max(maxPodsToShow - itemIdsToDisplay.length, 0);

    const placeholderPods = category.products.slice(category.products.length - numPlaceholderPods).map((product) => (
      <ProductPodPlaceholder podsCount={1} key={`placeholder-pod-${product.itemId}`} />
    ));

    if (!isFetchAllItemIds || isLoading) newCarouselContent.push(...placeholderPods);

    if (category.products.length > MAX_NUM_ITEMS_TO_LOAD && !simpleBanner) {
      newCarouselContent.push(
        <div
          className={cNames('special-buy-category__see-all')}
          key={`see-all-${category.categoryId}`}
          // id property is required by analytics
          id={specialBuyType === 'sbotd' ? 'SBOTD' : 'SBOTW'}
        >
          <a className={cNames('u__bold')} href={`${specialBuyEndpoint}#${category.categoryId}`}>
            See All {category.name} {{
              sbotd: 'Special Buy Of The Day',
              decor: 'Decor Special Buy Of The Week',
              pro: 'Pro Special Buy Of The Week',
            }[specialBuyType]}&nbsp;
            <Image src="https://assets.thdstatic.com/images/v1/caret-orange.svg" alt="" />
          </a>
        </div>,
      );
    }

    // only add something if the podCount * podWidth > bannerWidth
    if (!simpleBanner && (productPods.length * productPodWidth) > bannerWidth) {
      newCarouselContent.push(
        // pad carousel with empty items to prevent accidental navs
        // and to work around carousel bug
        null
      );
    }

    return newCarouselContent;
  }, [
    itemIdsToDisplay,
    storeId,
    isLoading,
    isFetchAllItemIds,
    bannerWidth,
    category.categoryId,
    category.name,
    category.products,
    specialBuyEndpoint,
    specialBuyType,
    simpleBanner
  ]); // this is a lot of dependencies, but the only ones to change
  // under ordinary conditions will be the first 4

  const categoryClassNames = cNames('special-buy-category', {
    'u--hide': !isSelected,
  });

  const itemClassNames = cNames('fixed-item', {
    'simple-carousel-product': simpleBanner
  });

  return (
    <article className={categoryClassNames}>
      {carouselContent.length ? (
        <QueryProvider
          queryOptions={optionsHandler}
          defaultVariables={{ storeId, loyaltyMembershipInput: null }}
          cacheKey="special-buy-carousel"
          dataSource="searchNav"
        >
          <Carousel
            transparent={simpleBanner}
            itemClass={itemClassNames}
            fixedItem
            multiItem={false}
            itemWidthPixelsMobile={productPodWidth}
            itemWidthPixelsDesktop={productPodWidth}
            onNextClick={!isFetchAllItemIds
              ? () => setIsFetchAllItemIds(true)
              : undefined}
          >
            {carouselContent}
          </Carousel>
        </QueryProvider>
      ) : (
        <ProductPodPlaceholder />
      )}
    </article>
  );
};

CategoryProducts.displayName = 'CategoryProducts';

CategoryProducts.propTypes = {
  category: shape({
    name: string.isRequired,
    products: arrayOf(shape({ image: string.isRequired })),
    categoryId: string,
  }).isRequired,
  bannerWidth: number
};

CategoryProducts.defaultProps = {
  bannerWidth: 0
};

export { CategoryProducts };
